import React, { useState } from 'react';
import type { Checklist } from '../types/checklist.types';
import { DataTable } from '@/components/ui/datatable';
import ChecklistCompleteDialog from './checklist-complete-dialog';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { getChecklistColumns } from './checklist-table-columns';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface ChecklistListProps {
  checklists: Checklist[];
  loading?: boolean;
  error?: string | null;
  onEdit?: (checklist: Checklist) => void;
  onDelete?: (checklist: Checklist) => void;
  onViewHistory?: (checklist: Checklist) => void;
}

import { useChecklistContext } from '../context/checklist-context';

export const ChecklistList: React.FC<ChecklistListProps> = ({ checklists, loading, error, onEdit, onDelete, onViewHistory }) => {
  // Use the shared context so refreshes update the main provider state
  const { submitChecklist, refreshChecklists } = useChecklistContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);

  if (loading) {
    return <div className="py-8 text-center">Loading checklists...</div>;
  }

  if (error) {
    return <div className="py-8 text-center text-destructive">{error}</div>;
  }

  if (!checklists || checklists.length === 0) {
    return <div className="py-8 text-center text-muted-foreground">No checklists found.</div>;
  }

  const handleViewComplete = (checklist: Checklist) => {
    setSelectedChecklist(checklist);
    setDialogOpen(true);
  };

  return (
    <>
      <DataTable
        columns={getChecklistColumns(onEdit, handleViewComplete, onDelete, onViewHistory)}
        data={checklists}
        pageSize={10}
        sortingId={TOUR_STEP_IDS.CHECKLISTS_SORTING_BUTTONS}
      />
      <ChecklistCompleteDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setSelectedChecklist(null);
        }}
        checklist={selectedChecklist}
        onSubmit={async (values, findings) => {
          if (!selectedChecklist) return;
          // Ensure findings are also saved alongside values
          await submitChecklist(selectedChecklist.id, values, undefined, undefined, findings);
          await refreshChecklists();
        }}
      />
    </>
  );
};
